import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Spinner from '@cloudscape-design/components/spinner';
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { exchangeAuthorizationCode, readAndClearSsoState } from './auth-adapter.js';

/**
 * OIDC Authorization Code callback landing page. vex-id redirects the browser
 * to /sso/callback?code=...&state=... after the user completes login+consent.
 *
 * Validates state (anti-CSRF), exchanges the code for an access_token via
 * the console-proxied /token endpoint, persists the token, then hard-reloads
 * `/` so AuthProvider re-hydrates the session cleanly (avoids stale in-memory
 * state from the pre-redirect app instance).
 *
 * On failure, sends the user back to /login?sso_error=... with a readable
 * message.
 */
export function SsoCallback(): ReactNode {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [statusMessage, setStatusMessage] = useState('Completing sign-in…');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const code = params.get('code');
    const returnedState = params.get('state');
    const errorParam = params.get('error');
    const errorDescription = params.get('error_description') ?? params.get('error');

    if (errorParam) {
      goBackWithError(navigate, errorDescription ?? errorParam);
      return;
    }
    if (!code) {
      goBackWithError(navigate, 'no authorization code returned');
      return;
    }
    const expectedState = readAndClearSsoState();
    if (expectedState && returnedState !== expectedState) {
      goBackWithError(navigate, 'state mismatch — possible CSRF, refusing token exchange');
      return;
    }

    const redirectUri = `${window.location.origin}/sso/callback`;
    exchangeAuthorizationCode(code, redirectUri)
      .then(() => {
        setStatusMessage('Signed in — loading console…');
        // Hard reload → AuthProvider re-runs whoami() and picks up the fresh JWT.
        window.location.replace('/');
      })
      .catch((e) => {
        setFailed(true);
        goBackWithError(navigate, e instanceof Error ? e.message : String(e));
      });
  }, [navigate, params]);

  return (
    <Box padding="xxxl">
      <div style={{ maxWidth: 420, margin: '120px auto 0' }}>
        <Container
          header={
            <Header variant="h1" description="Finalizing Vextura SSO">
              Sign-in
            </Header>
          }
        >
          {failed ? (
            <Alert type="error">Redirecting you back to sign in…</Alert>
          ) : (
            <Box textAlign="center" padding="l">
              <Spinner size="large" /> <Box variant="p">{statusMessage}</Box>
            </Box>
          )}
        </Container>
      </div>
    </Box>
  );
}

function goBackWithError(navigate: ReturnType<typeof useNavigate>, msg: string): void {
  const q = new URLSearchParams({ sso_error: msg });
  navigate(`/signin?${q.toString()}`, { replace: true });
}
